/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from "react";
import { ListChecks, UserCheck, Sparkles, Files, Mail, ArrowRight } from "lucide-react";

interface HowItWorksProps {
  onJoinWaitlist: () => void;
  onExploreLookbook: () => void;
}

export default function HowItWorks({ onJoinWaitlist, onExploreLookbook }: HowItWorksProps) {
  const processSteps = [
    {
      num: "01 / Apply Online",
      title: "Join the Waitlist",
      description: "The process begins with our simple waitlist application. Tell us about your wedding, your vision, and what matters most to you. We review every application carefully to ensure we are a good fit for your celebration.",
      icon: <Files size={15} />
    },
    {
      num: "02 / The Invitation",
      title: "Private Consultation",
      description: "If we have availability on your date, and we believe we are a match for your vision, we will invite you to a private virtual consultation. We'll discuss your wedding in detail, share more about our approach, and answer any questions you have.",
      icon: <Sparkles size={15} />
    },
    {
      num: "03 / The Booking",
      title: "Secure Your Date",
      description: "Once we agree to work together, we will send you a private link to complete your reservation. You'll sign our digital commission agreement and make your reservation deposit or select our monthly structure to secure your date on our calendar.",
      icon: <UserCheck size={15} />
    }
  ];

  return (
    <section className="bg-[#F8F5EF] text-[#22201D] py-16 sm:py-24 px-4 sm:px-6 relative border-t border-b border-[#4A1722]/15" id="how-it-works-section">
      <div className="max-w-6xl mx-auto">
        
        {/* Page Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="text-[10px] tracking-[0.35em] uppercase text-[#4A1722] font-display flex items-center justify-center gap-1.5 mb-3 font-semibold">
            <Sparkles size={11} /> Exclusive Admissions Framework
          </span>
          <h2 className="font-serif text-[42px] sm:text-[60px] font-extralight tracking-tight leading-none text-[#241018]">
            The Journey
          </h2>
          <p className="font-sans font-light text-xs sm:text-sm text-neutral-500 max-w-xl mx-auto leading-relaxed uppercase tracking-wider mt-4">
            Our calendar is intentionally kept small to preserve a highly personal experience. We accept only a small collection of weddings each year worldwide.
          </p>
          <div className="w-16 h-[1px] bg-[#4A1722]/45 mx-auto my-6"></div>
        </div>

        {/* Process Flow Steps Blocks */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {processSteps.map((step, idx) => (
            <div 
              key={idx}
              id={`process-step-${idx + 1}`}
              className="border border-[#4A1722]/15 p-8 sm:p-10 bg-[#F2ECE3] flex flex-col justify-between hover:bg-white transition-all duration-500 hover:shadow-lg hover:-translate-y-1"
            >
              <div>
                <div className="flex justify-between items-center mb-8">
                  <span className="font-serif text-xs uppercase tracking-[0.25em] text-[#C89848] font-semibold">
                    {step.num}
                  </span>
                  <div className="w-10 h-10 rounded-full bg-[#F8F5EF] border border-[#4A1722]/15 flex items-center justify-center text-[#4A1722] shadow-sm">
                    {step.icon}
                  </div>
                </div>

                <h3 className="font-serif text-[22px] font-semibold text-[#241018] mb-4 leading-tight">
                  {step.title}
                </h3>
                
                <p className="text-sm text-zinc-700 leading-relaxed font-light font-serif">
                  {step.description}
                </p>
              </div>

              <div className="pt-5 border-t border-[#4A1722]/10 mt-8 flex justify-between items-baseline text-[9px] uppercase tracking-widest text-[#4A1722]">
                <span>Process Code</span>
                <span className="font-mono text-stone-400 font-semibold">0{idx + 1} // REGULARITY</span>
              </div>
            </div>
          ))}
        </div>

        {/* Informative Callout Banner */}
        <div className="border-2 border-[#4A1722] bg-[#F2ECE3] p-8 sm:p-12 mb-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="space-y-3 max-w-2xl">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#4A1722] font-semibold flex items-center gap-1.5 font-bold">
              <Mail size={12} className="animate-pulse" /> Admissions Protocol
            </span>
            <h4 className="font-serif text-[24px] font-semibold text-[#241018]">
              We take a limited number of weddings each year. Join our waitlist today.
            </h4>
            <p className="text-xs text-neutral-600 leading-relaxed">
              If you believe your visual style matches our artistic direction, we advise submitting your coordinates. Applications are reviewed sequentially.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 min-w-max">
            <button
              id="btn-how-works-waitlist"
              onClick={onJoinWaitlist}
              className="bg-[#4A1722] text-[#F8F5EF] hover:bg-[#241018] text-[10px] uppercase tracking-[0.2em] py-4.5 px-8 transition duration-500 cursor-pointer text-center font-display font-semibold min-h-[44px] flex items-center justify-center select-none border-none"
            >
              JOIN THE WAITLIST
            </button>
            <button
              id="btn-how-works-lookbook"
              onClick={onExploreLookbook}
              className="border border-[#4A1722] text-[#4A1722] hover:bg-[#4A1722] hover:text-[#F8F5EF] text-[10px] uppercase tracking-[0.2em] py-4.5 px-8 transition duration-500 cursor-pointer text-center font-display min-h-[44px] flex items-center justify-center select-none bg-transparent"
            >
              EXPLORE LOOKBOOK
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}
